import { Mic } from "lucide-react";
import type { JSX } from "react";
import { useCallback, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { underV3, V3_ICON_TILE } from "@/components/ui/v3/flagged";
import {
  type DictationMicrophone,
  getDictationMicrophone,
  listMicrophones,
  type MicrophoneInput,
  setDictationMicrophone,
} from "@/lib/dictationMicrophone";
import { cn } from "@/lib/utils";
import { SETTINGS_CARD } from "../redesign/SettingsCard";

const SAME_AS_RECORDING = "__same-as-recording__";

type Input = MicrophoneInput;

// Settings > Note defaults: "Dictation microphone". "Same as recording" is
// the default; a choice is remembered in this browser (lib/dictationMicrophone).
export const DictationMicrophoneSetting = ({
  list = listMicrophones,
}: {
  // For tests and stories.
  list?: () => Promise<Input[]>;
}): JSX.Element => {
  const { t } = useTranslation();
  const id = useId();
  const [inputs, setInputs] = useState<Input[]>([]);
  const [chosen, setChosen] = useState<DictationMicrophone | null>(() =>
    getDictationMicrophone(),
  );

  const refresh = useCallback(async () => {
    try {
      setInputs(await list());
    } catch {
      setInputs([]);
    }
  }, [list]);

  useEffect(() => {
    void refresh();
    const onChange = (): void => {
      void refresh();
    };
    navigator.mediaDevices?.addEventListener?.("devicechange", onChange);
    return () =>
      navigator.mediaDevices?.removeEventListener?.("devicechange", onChange);
  }, [refresh]);

  const unnamed = inputs.length > 0 && inputs.every((input) => !input.label);
  const nameOf = (input: Input, index: number): string =>
    input.label ||
    t("settings.transcription.dictationMicrophone.unnamed", {
      n: index + 1,
    });
  const missing =
    chosen && !inputs.some((input) => input.deviceId === chosen.deviceId)
      ? chosen
      : null;

  // Names need microphone access once; the stream is closed right away.
  const allowNames = async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    } catch {
      // Blocked: the list keeps generic names.
    }
    await refresh();
  };

  const choose = (value: string): void => {
    if (value === SAME_AS_RECORDING) {
      setDictationMicrophone(null);
      setChosen(null);
      return;
    }
    const index = inputs.findIndex((input) => input.deviceId === value);
    const input = inputs[index];
    if (!input) return;
    const next = { deviceId: input.deviceId, label: nameOf(input, index) };
    setDictationMicrophone(next);
    setChosen(next);
  };

  return (
    <section
      aria-labelledby={`${id}-title`}
      className={
        AppConfig.enableAppRedesign
          ? SETTINGS_CARD
          : "rounded-[15px] border bg-background p-4 sm:p-6"
      }
      data-testid="settings-dictation-microphone"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full bg-v3-brand-tint text-v3-brand sm:size-11",
            underV3(V3_ICON_TILE),
            underV3("size-9 text-v3-text-2 sm:size-10 [&_svg]:size-[18px]"),
          )}
        >
          <Mic aria-hidden className="size-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <h3
            className={cn(
              "text-base font-bold leading-snug sm:text-lg",
              underV3(
                "font-v3-display text-[16px] font-semibold sm:text-[16px]",
              ),
            )}
            id={`${id}-title`}
          >
            {t("settings.transcription.dictationMicrophone.label")}
          </h3>
          <p
            className={cn(
              "text-sm text-muted-foreground",
              underV3("text-[14px] leading-relaxed text-v3-text-2"),
            )}
          >
            {t("settings.transcription.dictationMicrophone.hint")}
          </p>
        </div>
      </div>
      <div
        className={cn(
          "mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3",
          underV3("sm:ml-14"),
        )}
      >
        <Select
          value={chosen?.deviceId ?? SAME_AS_RECORDING}
          onValueChange={choose}
        >
          <SelectTrigger
            aria-labelledby={`${id}-title`}
            className="min-h-11 w-full sm:min-h-10 sm:max-w-sm"
            data-testid="settings-dictation-microphone-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SAME_AS_RECORDING}>
              {t("settings.transcription.dictationMicrophone.same")}
            </SelectItem>
            {inputs.map((input, index) => (
              <SelectItem key={input.deviceId} value={input.deviceId}>
                {nameOf(input, index)}
              </SelectItem>
            ))}
            {missing && (
              <SelectItem disabled value={missing.deviceId}>
                {t("settings.transcription.dictationMicrophone.missingSaved", {
                  name: missing.label || missing.deviceId.slice(0, 8),
                })}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        {unnamed && (
          <Button
            className="min-h-11 sm:min-h-10"
            type="button"
            variant="outline"
            onClick={() => void allowNames()}
          >
            {t("settings.transcription.dictationMicrophone.allow")}
          </Button>
        )}
      </div>
      {missing && (
        <p
          className="mt-2 text-sm text-v3-warn-text dark:text-v3-warn"
          role="status"
        >
          {t("settings.transcription.dictationMicrophone.missingNotice")}
        </p>
      )}
    </section>
  );
};
